import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo
} from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Tooltip,
  makeStyles,
  Button,
  Popover
} from '@material-ui/core';
import { DateTime } from 'luxon';
import { Link, useHistory } from 'react-router-dom';

import { getInitials, prettyBookingStatus } from 'helpers';
import {
  AssignmentRounded as LessonIcon,
  FeedbackRounded as FeedbackIcon,
  CallRounded as CallIcon,
  PublishRounded as SubmitIcon
} from '@mui/icons-material';

// Style
import styles from './styles';
import { AuthContext } from 'services/auth';
import BookingInfo from 'components/BookingInfo';
import { CallTimelineDialog } from 'components';
import { renderClor } from 'services/materials';
import { Chip } from '@mui/material';
import moment from 'moment-timezone';
import { withSnackbar } from 'notistack';

const useStyles = makeStyles(styles);

const GroupSessionBookingHistoryCard = memo(
  function GroupSessionBookingHistoryCard({
    groupSession,
    onSubmitFeedback,
    onViewFeedback,
    enqueueSnackbar
  }) {
    const classes = useStyles();
    const history = useHistory();
    const [data, setData] = useState({});
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const auth = useContext(AuthContext);
    const callTimelineDialogRef = useRef(null);

    // Test snackbar on mount
    useEffect(() => {
      if (enqueueSnackbar) {
        console.log('enqueueSnackbar is available on mount');
      } else {
        console.error('enqueueSnackbar is NOT available on mount');
      }
    }, [enqueueSnackbar]);

    const fetchData = useCallback(
      async (info) => {
        try {
          const [materialData, courseData] = await Promise.all([
            auth.api.materials.getMaterials(),
            auth.api.materials.getCourses(info.topic.material)
          ]);

          const material = materialData.find(
            (entry) => entry.id === info.topic.material
          );
          const course = courseData.find(
            (entry) => entry.id === info.topic.course
          );
          const lesson = course?.lessons?.find(
            (lesson) => lesson.id === info.topic.lesson
          );

          return { material, course, lesson };
        } catch (error) {
          console.error(error);
          return { error: 'Failed to fetch data' };
        }
      },
      [auth.api.materials]
    );

    const bookingIds = useMemo(
      () => groupSession?.bookings?.map((booking) => booking.bookingId) || [],
      [groupSession]
    );

    useEffect(() => {
      if (!groupSession?.bookings?.length) return;

      const fetchAllData = async () => {
        const newData = {};

        await Promise.all(
          groupSession.bookings.map(async (booking) => {
            const { bookingId, info } = booking;

            if (data[bookingId]) return;

            const bookingData = await fetchData(info);
            newData[bookingId] = bookingData;
          })
        );

        if (Object.keys(newData).length > 0) {
          setData((prevData) => ({ ...prevData, ...newData }));
        }
      };

      fetchAllData();
    }, [bookingIds, fetchData]);

    const handlePopoverClose = useCallback(() => {
      setPopoverAnchor(null);
    }, []);

    const handlePopoverOpen = useCallback((event) => {
      setPopoverAnchor(event.currentTarget);
    }, []);

    const handleCallLogOpen = useCallback((booking) => {
      callTimelineDialogRef.current.open(booking);
    }, []);

    const handleViewFeedback = useCallback(
      (booking) => {
        onViewFeedback({ ...booking, type: 'group' });
      },
      [onViewFeedback]
    );

    const handleSubmitFeedback = useCallback(
      (booking) => {
        onSubmitFeedback({ ...booking });
      },
      [onSubmitFeedback]
    );

    const renderedBookings = useMemo(() => {
      // Sort bookings by date in descending order (latest first)
      const sortedBookings = [...(groupSession?.bookings || [])].sort(
        (a, b) => {
          const aWhen = a.when || 0;
          const bWhen = b.when || 0;
          return bWhen - aWhen;
        }
      );

      return sortedBookings.map((booking) => {
        const {
          bookingId,
          status,
          info,
          tutor,
          when,
          studentIds = []
        } = booking;

        const sessionDate = DateTime.fromMillis(when || 0).toFormat(
          'd LLL yyyy, h:mm a'
        );
        const relativeDate = DateTime.fromMillis(when || 0).toRelative();

        const studentCount = studentIds.length;
        const bookingData = data[bookingId] || {};

        const isSubmitDisabled =
          Date.now() <= booking.when || booking.when + 86400000 < Date.now();
        const hasAllFeedback = booking?.students?.every(
          (item) => item?.feedback
        );
        const hasSomeFeedback = booking?.students?.some(
          (item) => item?.feedback
        );
        const hasCallLogs = booking?.logs && booking?.logs?.length > 0;

        return (
          <Card key={bookingId} style={{ marginBottom: '16px' }}>
            <CardContent>
              <Grid container spacing={4}>
                {/* Tutor Avatar */}
                <Grid
                  item
                  className={classes.avatarWrapper}
                  xs={12}
                  sm="auto"
                  md="auto"
                  lg="auto"
                  xl="auto">
                  <Tooltip title="Goto Profile" aria-label="Go to profile">
                    <Link to={`/teacher/profile/${tutor.uid}`}>
                      <Avatar
                        src={tutor.image}
                        alt={tutor.name}
                        className={classes.avatar}>
                        <Typography variant="h3" style={{ color: 'white' }}>
                          {getInitials(tutor.name || '')}
                        </Typography>
                      </Avatar>
                    </Link>
                  </Tooltip>
                </Grid>

                {/* Booking Information */}
                <Grid item xs={12} sm>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="flex-start">
                    {/* Name and Booking Status */}
                    <Grid item>
                      <Typography variant="h3" style={{ marginBottom: '10px' }}>
                        {bookingData?.course?.name}
                        &nbsp;
                        {bookingData?.lesson?.level}
                      </Typography>
                      <Typography variant="h3">{tutor.name}</Typography>
                      <div style={{ marginTop: '10px' }}>
                        {/* Status and Date/Time Information */}
                        <Grid container spacing={1} alignItems="center">
                          <Grid item>
                            <Chip
                              size="small"
                              label={prettyBookingStatus(status)}
                              color={renderClor(status)}
                            />
                          </Grid>
                          <Grid item>
                            <Typography color="textSecondary">
                              {`${sessionDate}, ${relativeDate}`}
                            </Typography>
                          </Grid>
                        </Grid>

                        {auth.role === 'tutor' && (
                          <div className={classes.actions}>
                            <Button
                              size="small"
                              className={classes.gutterRight}
                              variant="contained"
                              onClick={handlePopoverOpen}
                              startIcon={<LessonIcon />}>
                              view details
                            </Button>

                            {hasCallLogs && (
                              <Button
                                size="small"
                                className={`${classes.gutterRight} ${classes.hideOnSmall}`}
                                variant="outlined"
                                color="primary"
                                onClick={() => handleCallLogOpen(booking)}
                                startIcon={<CallIcon />}>
                                call log
                              </Button>
                            )}

                            {hasAllFeedback ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => handleViewFeedback(booking)}
                                startIcon={<FeedbackIcon />}>
                                view feedback
                              </Button>
                            ) : (
                              <>
                                {hasSomeFeedback && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    className={classes.gutterRight}
                                    onClick={() => handleViewFeedback(booking)}
                                    startIcon={<FeedbackIcon />}>
                                    view feedback
                                  </Button>
                                )}
                                {status === 'pending-feedback' && (
                                  <Button
                                    size="small"
                                    disabled={isSubmitDisabled}
                                    variant="contained"
                                    color="primary"
                                    onClick={() =>
                                      handleSubmitFeedback(booking)
                                    }
                                    startIcon={<SubmitIcon />}>
                                    submit feedback
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {auth.role === 'student' && (
                          <div className={classes.actions}>
                            <Button
                              size="small"
                              className={classes.gutterRight}
                              variant="contained"
                              onClick={handlePopoverOpen}
                              startIcon={<LessonIcon />}>
                              view details
                            </Button>

                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              disabled={!hasSomeFeedback}
                              onClick={() => handleViewFeedback(booking)}
                              startIcon={<FeedbackIcon />}>
                              view feedback
                            </Button>
                          </div>
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      });
    }, [
      groupSession,
      data,
      classes,
      auth.role,
      handlePopoverOpen,
      handleCallLogOpen,
      handleViewFeedback,
      handleSubmitFeedback
    ]);

    return (
      <Fragment>
        <Popover
          id={popoverAnchor ? 'simple-popover' : undefined}
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}>
          <BookingInfo
            info={groupSession?.bookings?.[0]?.info}
            booking={groupSession?.bookings?.[0]}
          />
        </Popover>

        <CallTimelineDialog ref={callTimelineDialogRef} isGroupSession={true} />

        {renderedBookings}
      </Fragment>
    );
  }
);

export default withSnackbar(GroupSessionBookingHistoryCard);
